import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';

export interface AchievementCondition {
  type: string;
  target: number;
  additionalParams?: any;
}

export interface AchievementDefinition {
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  condition: AchievementCondition;
  triggers: string[];
}

export interface UserAchievement {
  id: number;
  userID: number;
  achievementKey: string;
  progress: number;
  isCompleted: boolean;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  definition: AchievementDefinition | null;
}

export interface UserAchievementsResponse {
  achievements: UserAchievement[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class AchievementsBackendService {
  private readonly ACHIEVEMENTS_BASE_ADDRESS = '/achievements';

  /**
   * @description constructor of AchievementsBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description gets user achievements
   * @param {number} userID - user ID (optional, uses current user if not provided)
   * @param {boolean} completedOnly - only return completed achievements (default: false)
   * @returns {Promise<UserAchievementsResponse>}
   */
  public getUserAchievements(
    userID?: number,
    completedOnly: boolean = false
  ): Promise<UserAchievementsResponse> {
    let url = `${this.ACHIEVEMENTS_BASE_ADDRESS}/user`;
    const params = new URLSearchParams();
    
    if (userID) {
      params.append('userID', userID.toString());
    }
    
    if (completedOnly) {
      params.append('completedOnly', 'true');
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return firstValueFrom(this.http.get<UserAchievement[]>(url))
      .then(achievements => ({
        achievements,
        total: achievements.length
      }));
  }

  /**
   * @description gets completed achievements only
   * @param {number} userID - user ID (optional, uses current user if not provided)
   * @returns {Promise<UserAchievementsResponse>}
   */
  public getCompletedAchievements(userID?: number): Promise<UserAchievementsResponse> {
    return this.getUserAchievements(userID, true);
  }

  /**
   * @description gets achievement progress as percentage
   * @param {UserAchievement} achievement - the achievement object
   * @returns {number} progress percentage (0-100)
   */
  public getProgressPercentage(achievement: UserAchievement): number {
    if (!achievement.definition?.condition?.target) {
      return achievement.isCompleted ? 100 : 0;
    }
    
    const percentage = (achievement.progress / achievement.definition.condition.target) * 100;
    return Math.min(100, Math.max(0, percentage));
  }

  /**
   * @description formats achievement category
   * @param {string} category - the category string
   * @returns {string} formatted category
   */
  public formatCategory(category: string): string {
    switch (category.toLowerCase()) {
      case 'erste_schritte': return 'Erste Schritte';
      case 'spielerfolge': return 'Spielerfolge';
      case 'social': return 'Social';
      case 'meisterschaft': return 'Meisterschaft';
      case 'special': return 'Special';
      default: return category;
    }
  }

  /**
   * @description formats time ago from date
   * @param {Date | string} date - date object or ISO string
   * @returns {string} formatted time ago
   */
  public formatTimeAgo(date: Date | string): string {
    const achievementDate = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - achievementDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMinutes < 1) {
      return 'gerade eben';
    } else if (diffMinutes < 60) {
      return `vor ${diffMinutes} Min`;
    } else if (diffHours < 24) {
      return `vor ${diffHours} Std`;
    } else if (diffDays === 1) {
      return 'gestern';
    } else if (diffDays < 30) {
      return `vor ${diffDays} Tagen`;
    } else if (diffMonths === 1) {
      return 'vor 1 Monat';
    } else {
      return `vor ${diffMonths} Monaten`;
    }
  }

  /**
   * @description formats completion date
   * @param {Date | string} date - date object or ISO string
   * @returns {string} formatted date
   */
  public formatCompletionDate(date: Date | string): string {
    const achievementDate = typeof date === 'string' ? new Date(date) : date;
    return achievementDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  /**
   * @description sorts achievements by completion status and date
   * @param {UserAchievement[]} achievements - array of achievements
   * @returns {UserAchievement[]} sorted achievements
   */
  public sortAchievements(achievements: UserAchievement[]): UserAchievement[] {
    return achievements.sort((a, b) => {
      // Completed achievements first, sorted by completion date (newest first)
      if (a.isCompleted && b.isCompleted) {
        const aDate = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bDate = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        return bDate - aDate;
      }
      
      // Completed before uncompleted
      if (a.isCompleted && !b.isCompleted) return -1;
      if (!a.isCompleted && b.isCompleted) return 1;
      
      // For uncompleted, sort by progress (highest first)
      return b.progress - a.progress;
    });
  }

  /**
   * @description groups achievements by category
   * @param {UserAchievement[]} achievements - array of achievements
   * @returns {Record<string, UserAchievement[]>} achievements grouped by category
   */
  public groupByCategory(achievements: UserAchievement[]): Record<string, UserAchievement[]> {
    return achievements.reduce((groups, achievement) => {
      const category = achievement.definition?.category || 'Andere';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(achievement);
      return groups;
    }, {} as Record<string, UserAchievement[]>);
  }
}